import { API_get_certificate_types } from "../constants";
import { getRightworkAPI } from "../main/content/requests/utilities/requests";

export const ROLE_REQUIRED_CERTIFICATE_OPTIONS = [
  { value: "food_permit", label: "Food Permit" },
  { value: "liquor_permit", label: "Liquor Permit" },
  { value: "break_waiver", label: "Meal Period Waiver" },
];

export const CERTIFICATE_TYPE_OPTIONS = [
  ...ROLE_REQUIRED_CERTIFICATE_OPTIONS,
  { value: "emergency_contact", label: "Emergency Contact" },
  { value: "other", label: "Other" },
];

export const CERTIFICATE_TYPE_LABELS = CERTIFICATE_TYPE_OPTIONS.reduce(
  (acc, option) => {
    acc[option.value] = option.label;
    return acc;
  },
  {}
);

// Build a {value -> label} lookup from a fetched catalog, merged over the bundled
// defaults so built-in slugs (e.g. break_waiver / emergency_contact) always resolve
// their label even if a store's feature flag hides them from the fetched catalog.
export const buildCertificateTypeLabels = (types) => {
  if (!Array.isArray(types) || types.length === 0) {
    return CERTIFICATE_TYPE_LABELS;
  }
  return types.reduce(
    (acc, t) => {
      acc[t.value] = t.label;
      return acc;
    },
    { ...CERTIFICATE_TYPE_LABELS }
  );
};

// Deterministic Tailwind color pairs for custom types that have no explicit color.
const CUSTOM_TYPE_COLOR_PALETTE = [
  { bgColor: "bg-teal-100", iconColor: "text-teal-600" },
  { bgColor: "bg-indigo-100", iconColor: "text-indigo-600" },
  { bgColor: "bg-emerald-100", iconColor: "text-emerald-600" },
  { bgColor: "bg-orange-100", iconColor: "text-orange-600" },
  { bgColor: "bg-cyan-100", iconColor: "text-cyan-600" },
  { bgColor: "bg-fuchsia-100", iconColor: "text-fuchsia-600" },
];

export const getCustomTypeColor = (slug) => {
  const key = String(slug || "");
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return CUSTOM_TYPE_COLOR_PALETTE[hash % CUSTOM_TYPE_COLOR_PALETTE.length];
};

// Fetch the certificate-type catalog for a store, shaped for the LMS mapping
// pickers (the documentUpload block and the track Certification setting).
//  - options: types the pickers may OFFER — mappable, uploadable, and not
//    hidden from the Certificates overview. This predicate is a contract with
//    the backend validator (certificate_info.utils.get_lms_mappable_slugs
//    plus the hidden gate); keep them aligned.
//  - labelBySlug: labels for the WHOLE catalog (hidden types included, over
//    the bundled defaults), for resolving a stored slug the picker no longer
//    offers. Archived types are absent — a missing label means archived.
export const fetchLmsMappableCertificateTypes = async (storeId) => {
  const res = await getRightworkAPI(API_get_certificate_types, {
    store_id: storeId,
  });
  if (res.status !== 200) {
    return { options: [], labelBySlug: CERTIFICATE_TYPE_LABELS, loaded: false };
  }
  const catalog = res.data?.redux?.payload?.certificate_types || [];
  return {
    options: catalog.filter(
      (t) => t.lms_mappable && t.uploadable && !t.overview_hidden
    ),
    labelBySlug: buildCertificateTypeLabels(catalog),
    loaded: true,
  };
};
